'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { Icon, Toggletip, ToggletipContent, ToggletipTrigger } from '@repo/ui'
import { Info } from '@repo/ui/icons'
import { memo, type ReactElement, type ReactNode } from 'react'

export interface InfoTooltipProps {
  /** Content to display in the tooltip */
  tooltipContent?: ReactNode
  /**
   * Whether to disable hoverable content (prevents tooltip staying open when hovered). Only
   * applies without touch input: on touch devices the content opens on tap instead of hover.
   */
  disableHoverableContent?: boolean
  /** Use warning (error) colors for the info icon */
  isWarning?: boolean
  /** Whether the tooltip is disabled */
  disabled?: boolean
  /**
   * Offset from the trigger element in pixels.
   * Note: kept for v5 API parity — v5 declared this prop but never forwarded
   * it to the underlying Tooltip, so it has no effect.
   */
  offset?: number
  /** Which side to show the tooltip on */
  side?: 'top' | 'bottom' | 'left' | 'right'
  /**
   * Trigger glyph, defaulting to the Info icon. A row whose own glyph carries meaning — the
   * warning triangle on the Fee row — passes its own rather than duplicating the keyboard
   * affordances below.
   */
  icon?: ReactElement
  /** Trigger colours, replacing the default / `isWarning` pair for the same reason. */
  className?: string
}

/**
 * InfoTooltip component - Tailwind replica of dapp-ui-v5 InfoTooltip
 *
 * Thin wrapper over @repo/ui Toggletip rendering an Info icon as the trigger: a hover tooltip
 * without touch input, a tap-to-open popover with it.
 *
 * Matches v5 StyledInfoIcon colors:
 * - default: icon.secondary, hover: icon.primary
 * - isWarning: icon.interactive.error, hover: constant.error
 *
 * @example
 * ```tsx
 * <InfoTooltip tooltipContent="More information" />
 * <InfoTooltip tooltipContent="Something is wrong" isWarning />
 * ```
 */
function InfoTooltip({
  tooltipContent,
  disableHoverableContent,
  isWarning = false,
  disabled,
  side,
  icon = <Info />,
  className
}: InfoTooltipProps) {
  return (
    <Toggletip disableHoverableContent={disableHoverableContent}>
      <ToggletipTrigger asChild>
        {/* The Icon is the trigger itself. Wrapping it in a plain block div made the icon box
            taller than the glyph, so it sat low against its label — hence button semantics on
            the icon rather than a real <button> around it, which also keeps this safe to render
            inside the interactive rows that already nest popovers. Radix opens the tooltip on
            focus and closes it on Escape, so tabbing to it is the whole keyboard interaction:
            without `tabIndex` there was no way to read any of these explanations by keyboard. */}
        <Icon
          role="button"
          tabIndex={0}
          aria-label="More information"
          className={cn(
            'focus-visible:ring-ring/50 rounded-sm outline-none focus-visible:ring-[3px]',
            className ??
              (isWarning
                ? 'text-destructive-foreground hover:text-destructive-foreground'
                : 'text-secondary-foreground hover:text-foreground focus-visible:text-foreground')
          )}
          icon={icon}
        />
      </ToggletipTrigger>
      {!disabled && <ToggletipContent side={side}>{tooltipContent}</ToggletipContent>}
    </Toggletip>
  )
}

const MemoizedInfoTooltip = memo(InfoTooltip)
MemoizedInfoTooltip.displayName = 'InfoTooltip'

export { MemoizedInfoTooltip as InfoTooltip }
